import { FC, ReactNode, useEffect, useMemo, useState } from "react";

import { ArrowBack, InfoOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Mark43ApprovalStatus,
  Mark43ReportEndpoint,
  OrgConfigMetadata,
  OrgConfigModel,
  ResponseNotes,
  getOrgConfigPortal,
  patchOrgConfigAdmin,
} from "@rubi/api-generated";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { Breadcrumbs, Crumb, DevPage } from "./common";
import {
  BoolField,
  ChipListField,
  ColourField,
  FieldGroup,
  KeyedMapField,
  NotifyItemsField,
  PairListField,
  ResponseNoteField,
  StatusNotifyGroupsField,
  SuggestField,
  TemplateListField,
  TitleUrlField,
} from "./configFields";
import { ConfigFieldIndexEntry, ConfigSectionNav } from "./ConfigSectionNav";
import { M2mKeysPanel } from "./M2mKeysPanel";
import { OrgBrandingPanel } from "./OrgBrandingPanel";
import {
  resolveSection,
  SELF_SAVING_TABS,
  TAB_GROUPS,
  TAB_LABEL,
  TabKey,
} from "./orgConfigSections";
import { OrgDirectoryPanel } from "./OrgDirectoryPanel";
import { SsoConnectionsPanel } from "./SsoConnectionsPanel";
import { useFieldFlash } from "./useFieldFlash";

import "./DevOrgConfigPage.scss";

const METADATA_FIELD_TO_TAB: Partial<Record<keyof OrgConfigMetadata, TabKey>> =
  {
    city: "general",
    contact_email: "general",
    use_contact_email_for_responses: "general",
    cc_responding_user: "general",
    emergency_number: "general",
    nonemergency_number: "general",
    address: "general",
    timezone: "general",

    frontend_url: "urls",
    homepage_url: "urls",
    camera_registry_url: "urls",
    online_reporting_url: "urls",
    registries_url: "urls",
    supplemental_reporting_url: "urls",
    alternative_supplemental_direction: "urls",
    logo_url: "urls",

    tracking_number_prefix: "identifiers",
    ori_number: "identifiers",
    agency_code: "identifiers",
    automated_incident_number_generation: "identifiers",
    incident_number_pattern: "identifiers",
    incident_number_length: "identifiers",
    incident_number_regex: "identifiers",
    incident_generation_outcomes: "identifiers",
    rms_export_outcomes: "identifiers",
    dems_export_outcomes: "identifiers",

    include_logo_in_email_templates: "behavior",
    include_pdf_in_notification_emails: "behavior",
    enable_crime_codes: "behavior",
    display_address_on_pdf: "behavior",
    include_disclaimer_header_on_pdf: "behavior",
    require_synopsis_verification: "behavior",
    perform_attachment_conversion_to_pdf: "behavior",
    require_internal_user_id: "behavior",
    priority_options: "behavior",
    tag_options: "behavior",
    rms_export_model: "behavior",
    rms_export_badge_number_override: "behavior",
    use_survey_group_ui: "behavior",
    enable_attachment_title: "behavior",
    generate_attachment_descriptions: "behavior",
    disable_modification_request: "behavior",
    attachment_type_item_type_option_id: "behavior",

    collision_center_url: "citizen",
    non_technical_issues_website: "citizen",
    hide_landing_page_report_now_button: "citizen",
    header_colour_override: "citizen",
    temporary_online_reporting_override: "citizen",
    citizen_engage_disable_hidden_contact_number: "citizen",
    citizen_engage_disable_reports_contact_number: "citizen",

    units: "lists",
    region_filter_options: "lists",
    field_configurations: "lists",
  };

const COLUMN_FIELD_TO_TAB: Partial<Record<keyof OrgConfigModel, TabKey>> = {
  tracking_number_seq: "columns",
  incident_number_seq: "columns",
  ivr_inbound_number: "columns",
  citizen_engage_general_reporting_enabled: "columns",
  supported_languages: "columns",
  rejection_reasons: "columns",
  notify: "notify",
  templates: "templates",
  export_configuration: "exports",
  response_notes: "notes",
  // `metadata` is intentionally not mapped here; metadata changes already
  // light up the typed tabs (general/urls/etc.). Mapping it would double-flag
  // every typed edit on the JSON tab too.
};

// One-line description of each tab, shown as an intro banner. Aimed at a
// dev-admin opening the page for the first time.
const TAB_INTRO: Record<TabKey, string> = {
  general:
    "Org contact info and how reply emails are routed. These values appear in citizen-facing emails and the public portal.",
  urls: "External links surfaced in emails and the citizen portal. Placeholders left over from bootstrap will be obvious here.",
  identifiers:
    "Tracking numbers, incident numbers, and the agency codes (ORI / agency_code) baked into RMS exports.",
  behavior:
    "Feature toggles and rendering options for emails, PDFs, and RMS exports. Also where tag and priority option lists live.",
  citizen:
    "What the public services site shows for this agency — collision reporting, the non-technical issues link, header colour, and the contact numbers/CTA it hides. Reads through the citizen block of GET /public/community.",
  lists:
    "Option lists the report forms draw from: units, region filters, and the per-field configuration blob.",
  notes:
    "Status-change note copy sent to citizens, in English and French. Empty body and footer clears a note.",
  notify:
    "Who gets emailed about submissions. General lists apply to every report of a class ('all' matches everything); per-status lists only fire once a report reaches that status.",
  templates:
    "Named plain-text replies reviewers pick from when responding to a submission. Sent as written — placeholders in **double asterisks** are filled in by the reviewer before sending.",
  exports:
    "How approved reports leave Rubicon: the RMS record export and the DEMS/Axon evidence export. Each half is independent — leave one off if the agency doesn't use it. Credentials are not stored here.",
  columns:
    "Direct columns on rv2_org_config that aren't part of the metadata blob — sequence counters, IVR routing, supported languages, etc.",
  json: "The full metadata blob, kept as an escape hatch for anything the typed tabs don't expose. Saving here overwrites the typed tabs.",
  api_keys: "Machine-to-machine API credentials issued to this org.",
  sso: "SAML SSO connections (identity providers) the org's members can sign in with.",
  directory:
    "Public directory & discovery fields on rubi_org — SSO slug/provider, services slug, province, map placement, and enabled apps. These drive the login dropdown, services map, and SSO discovery. Saved independently of the config fields above.",
  branding:
    "Branding assets (crest, login logo/background, hero, crest+wordmark) served from S3. View the current assets and upload replacements. Saved independently of the config fields above.",
};

// Field-level explanations sourced from how the field is actually used in the
// code (api-rs handlers + Python email templates). Keep these short — they
// surface as tooltips on (i) icons next to the field.
const FIELD_HELP: Record<string, string> = {
  // General
  use_contact_email_for_responses:
    "When true, replies to citizens come from this org's contact_email under a generic 'Online Reporting Team' sender. When false, replies come from the individual responding user.",
  cc_responding_user:
    "When sending replies from the org contact email, also CC the responding officer so they see follow-ups.",

  // URLs
  frontend_url:
    "Base URL of the internal portal (used in admin-facing links inside emails).",
  homepage_url:
    "Agency's main public website. Surfaced in all citizen-facing emails as the primary contact point.",
  camera_registry_url:
    "Public camera registry page. Linked in camera registration / removal emails.",
  online_reporting_url:
    "Where citizens go to start a new online report. Linked in confirmation and status emails.",
  registries_url:
    "Optional third-party registries link surfaced in some email templates.",
  supplemental_reporting_url:
    "Where citizens file supplemental / follow-up info on an existing report.",
  alternative_supplemental_direction:
    "Free-text fallback shown when there's no supplemental_reporting_url — e.g. 'Call our non-emergency line to add details.'",
  logo_url:
    "Agency logo. Embedded in emails only when 'Include logo in email templates' is on.",

  // Identifiers
  tracking_number_prefix:
    "Fixed prefix for auto-issued tracking numbers (e.g. 'PD' → PD-000001). Tracking numbers identify a citizen submission; incident numbers identify the RMS record.",
  ori_number:
    "ORI (Originating Agency Identifier) — the FBI/CPIC code for this agency. Sent to RMS systems on export.",
  agency_code:
    "Internal agency identifier passed through in RMS export payloads.",
  automated_incident_number_generation:
    "When on, an incident number is auto-generated from the pattern/length below on RMS export. Off means incidents must be numbered manually.",
  incident_number_pattern:
    "Template, e.g. BR{YY}{tracking_number}. Supported tokens: {YYYY} (4-digit year), {YY} (2-digit year), {tracking_number} (auto-incrementing counter). Any other text is used literally — unrecognised tokens are NOT substituted.",
  incident_number_length:
    "Digit width of the {tracking_number} portion, zero-padded. Both this and the pattern must be set for generation. With no explicit regex below, the two also gate what officers may type into the incident number field, where the counter must be exactly this many digits.",
  incident_number_regex:
    "Regex for what the portal ACCEPTS in the incident number field, e.g. \\d{4}-\\d{6}. Set this when officers legitimately enter numbers the generator would never emit (legacy or externally-issued records) — it fully replaces the pattern/length check. Matched against the whole value and case-sensitively; no need to add ^ or $. Leave blank to keep inferring the accepted shape from the pattern and length.",
  incident_generation_outcomes:
    "Submission statuses that trigger incident number generation. One value per line, or comma-separated.",
  rms_export_outcomes:
    "Submission statuses that are allowed to flow through to RMS export. Acts as an allow-list filter.",
  dems_export_outcomes:
    "Submission statuses that are allowed to flow through to DEMS / Axon export.",

  // Behavior
  include_logo_in_email_templates:
    "When on, logo_url is embedded in outbound email templates.",
  include_pdf_in_notification_emails:
    "When on, a PDF copy of the report is attached to status-update emails.",
  enable_crime_codes:
    "When on, submitted reports are auto-categorized with crime/incident codes via AI analysis.",
  display_address_on_pdf:
    "When on, the org's address (General tab) is printed on generated report PDFs.",
  include_disclaimer_header_on_pdf:
    "When on, a legal/disclaimer header is added to the top of report PDFs.",
  require_synopsis_verification:
    "When on, the submission synopsis (incident summary) must be manually verified before RMS export.",
  perform_attachment_conversion_to_pdf:
    "When on, citizen-uploaded attachments (images, docs) are converted to PDF before being bundled into the report.",
  require_internal_user_id:
    "When on, the user profile shows an Internal User ID field and requires it. Pair with 'Use the user's internal ID as the badge' on the Exports tab to send it to the RMS.",
  rms_export_model:
    "Which RMS vendor's XML schema to emit. Known values: tyler, optic_cl, optic_ocr, versadex.",
  rms_export_badge_number_override:
    "Alternate field name to use for officer badge / ID in RMS exports when the default doesn't match the vendor.",
  tag_options:
    "Pre-defined tag values selectable on submissions (e.g. Arson, Theft).",
  priority_options:
    "Pre-defined priority levels (name + numeric value) available for submission triage.",

  // Other columns
  tracking_number_seq:
    "Counter that increments each time a tracking number is issued. Don't decrease this — duplicates will result.",
  incident_number_seq:
    "Counter that increments each time an incident number is auto-generated.",
  ivr_inbound_number:
    "Phone number that the IVR (interactive voice response) system uses to route inbound calls to this org.",
  citizen_engage_general_reporting_enabled:
    "When on, enables the general (unstructured) report submission flow in Citizen Engage.",
  supported_languages:
    'ISO language codes for surveys and portal UI, e.g. ["en", "fr"].',
  rejection_reasons:
    "Pre-defined reason strings shown when rejecting a submission.",
};

// Searchable field index for the sidebar. Derived from the same maps that drive
// the dirty markers, so a field can never be searchable but unroutable (or the
// reverse). Help text is included in the corpus so prose finds a field whose key
// you don't know.
const FIELD_INDEX: ConfigFieldIndexEntry<TabKey>[] = [
  ...Object.entries(METADATA_FIELD_TO_TAB).map(([field, tab]) => ({
    field,
    tab: tab as TabKey,
    help: FIELD_HELP[field],
  })),
  ...Object.entries(COLUMN_FIELD_TO_TAB).map(([field, tab]) => ({
    field,
    tab: tab as TabKey,
    help: FIELD_HELP[field],
  })),
];

const InfoTip: FC<{ text: string | undefined }> = ({ text }) => {
  if (!text) return null;
  return (
    <Tooltip
      title={text}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: 14,
            lineHeight: 1.5,
            maxWidth: 360,
            px: 1.5,
            py: 1,
          },
        },
      }}
    >
      <InfoOutlined
        sx={{
          fontSize: 20,
          color: "action.active",
          cursor: "help",
          opacity: 0.7,
          "&:hover": { opacity: 1 },
        }}
      />
    </Tooltip>
  );
};

// Builds `InputProps` that tucks an info tooltip into a TextField's
// end-adornment. Returns undefined when there's no help text so we don't
// inject empty adornments.
const helpAdornment = (key: string) => {
  const text = FIELD_HELP[key];
  if (!text) return undefined;
  return {
    endAdornment: (
      <InputAdornment position="end">
        <InfoTip text={text} />
      </InputAdornment>
    ),
  };
};

const LabelWithInfo: FC<{ label: string; helpKey: string }> = ({
  label,
  helpKey,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.75}
    component="span"
    sx={{ verticalAlign: "middle" }}
  >
    <span>{label}</span>
    <InfoTip text={FIELD_HELP[helpKey]} />
  </Stack>
);

const TabIntro: FC<{ tab: TabKey }> = ({ tab }) => (
  <Alert
    severity="info"
    icon={<InfoOutlined fontSize="medium" />}
    sx={{
      mb: 3,
      py: 1,
      "& .MuiAlert-message": { fontSize: 15, lineHeight: 1.5, py: 0.5 },
    }}
  >
    {TAB_INTRO[tab]}
  </Alert>
);

const stableJson = (v: unknown): string => {
  try {
    return JSON.stringify(v ?? null);
  } catch {
    return "";
  }
};

const formatJson = (v: unknown): string => {
  if (v == null) return "";
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return "";
  }
};

// The two list columns are typed `unknown` on the model (plain JSONB), so narrow
// them for the chip editor and drop anything that isn't a string.
const asStringList = (v: unknown): string[] | null =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : null;

// Response-note slots, in the order they're shown. EN and FR sit side by side so
// a missing translation is obvious.
const RESPONSE_NOTE_SLOTS: { key: keyof ResponseNotes; label: string }[] = [
  { key: "on_approved", label: "Approved (EN)" },
  { key: "on_approved_fr", label: "Approved (FR)" },
  { key: "on_rejected", label: "Rejected (EN)" },
  { key: "on_rejected_fr", label: "Rejected (FR)" },
  { key: "on_escalated", label: "Escalated (EN)" },
  { key: "on_escalated_fr", label: "Escalated (FR)" },
  { key: "on_confirmation", label: "Confirmation (EN)" },
  { key: "on_confirmation_fr", label: "Confirmation (FR)" },
];

// Values seen in the live configs. Suggestions only — a new provider or model
// must not require a code change here.
const RMS_PROVIDERS = ["niche", "versaterm", "mark43"];

// Matches the backend's RmsExportConfiguration::is_mark43, which compares
// case-insensitively with surrounding whitespace ignored — `provider` is free
// text that has never been validated on write, so existing rows carry values
// like "Niche" and "versaterm" interchangeably.
const isMark43Provider = (provider: string) =>
  provider.trim().toLowerCase() === "mark43";
const RMS_EXPORT_MODELS = ["optic_cl", "optic_ocr", "versadex", "versaterm"];
const DEMS_PROVIDERS = ["evidence.com", "local_directory"];

export const DevOrgConfigPage: FC = () => {
  const { id: orgId } = useParams();
  const navigate = useNavigate();

  const [config, setConfig] = useState<OrgConfigModel | null>(null);
  const [originalConfig, setOriginalConfig] = useState<OrgConfigModel | null>(
    null,
  );
  // Section lives in the URL so a section can be linked to and survives a
  // reload. `replace` keeps 16 tab clicks out of the back stack while still
  // updating the address bar.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveSection(searchParams.get("section"));
  const setActiveTab = (key: TabKey, field?: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("section", key);
    setSearchParams(next, { replace: true });
    // Deliberately not in the URL: a link should open a section, not re-trigger
    // a one-off scroll/flash on every reload.
    setPendingField(field ?? null);
  };

  // Search hits scroll to their input once the target section has rendered.
  const [pendingField, setPendingField] = useState<string | null>(null);
  useFieldFlash(pendingField, activeTab, () => setPendingField(null));

  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);

  // Raw JSON state for the JSON-column editors, so users can type invalid JSON
  // mid-edit without losing it. Validated on blur / save.
  const [metadataJson, setMetadataJson] = useState("");
  const [fieldConfigurationsJson, setFieldConfigurationsJson] = useState("");

  const [jsonErrors, setJsonErrors] = useState<Record<string, string | null>>(
    {},
  );

  useEffect(() => {
    if (!orgId) return;
    const fetchConfig = async () => {
      try {
        const response = await getOrgConfigPortal({
          path: { org_id: orgId },
        });
        const data = response.data!;
        setConfig(data);
        setOriginalConfig(data);
        setFieldConfigurationsJson(
          formatJson(data.metadata.field_configurations),
        );
        setMetadataJson(formatJson(data.metadata));
        setLoadError(null);
      } catch (err) {
        console.error(err);
        setLoadError(
          err instanceof Error ? err.message : "Failed to load org config",
        );
      }
    };
    fetchConfig();
  }, [orgId]);

  const updateMetadata = (patch: Partial<OrgConfigMetadata>) => {
    setConfig((prev) =>
      prev ? { ...prev, metadata: { ...prev.metadata, ...patch } } : prev,
    );
  };

  const updateColumn = <K extends keyof OrgConfigModel>(
    key: K,
    value: OrgConfigModel[K],
  ) => {
    setConfig((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const setJsonError = (field: string, msg: string | null) =>
    setJsonErrors((prev) => ({ ...prev, [field]: msg }));

  const tryParseJson = <T,>(
    field: string,
    raw: string,
    allowEmpty: boolean,
  ): { ok: true; value: T | null } | { ok: false } => {
    const trimmed = raw.trim();
    if (trimmed === "") {
      if (allowEmpty) {
        setJsonError(field, null);
        return { ok: true, value: null };
      }
      setJsonError(field, "Required");
      return { ok: false };
    }
    try {
      const parsed = JSON.parse(trimmed) as T;
      setJsonError(field, null);
      return { ok: true, value: parsed };
    } catch (e) {
      setJsonError(field, e instanceof Error ? e.message : "Invalid JSON");
      return { ok: false };
    }
  };

  // Apply any pending JSON-editor changes back to the typed config. Returns
  // false if anything failed to parse.
  const syncJsonFieldsIntoConfig = (): boolean => {
    if (!config) return false;
    let ok = true;
    const next: OrgConfigModel = { ...config };

    const metaResult = tryParseJson<OrgConfigMetadata>(
      "metadata",
      metadataJson,
      false,
    );
    if (!metaResult.ok) ok = false;
    else if (metaResult.value) next.metadata = metaResult.value;

    // field_configurations is deliberately NOT synced here. Its editor parses on
    // change and writes straight into config.metadata, and a bad parse leaves an
    // entry in jsonErrors which already blocks the save. Re-applying it after the
    // metadata block above would let a stale buffer overwrite an edit made in the
    // full-metadata editor.

    if (ok) setConfig(next);
    return ok;
  };

  // Build the per-tab dirty markers.  Compare each tracked field against the
  // original; any difference flips the dot on the owning tab.
  const dirtyTabs = useMemo(() => {
    const dirty = new Set<TabKey>();
    if (!config || !originalConfig) return dirty;

    (Object.keys(METADATA_FIELD_TO_TAB) as (keyof OrgConfigMetadata)[]).forEach(
      (field) => {
        const tab = METADATA_FIELD_TO_TAB[field];
        if (!tab) return;
        if (
          stableJson(config.metadata[field]) !==
          stableJson(originalConfig.metadata[field])
        ) {
          dirty.add(tab);
        }
      },
    );
    (Object.keys(COLUMN_FIELD_TO_TAB) as (keyof OrgConfigModel)[]).forEach(
      (field) => {
        const tab = COLUMN_FIELD_TO_TAB[field];
        if (!tab) return;
        if (stableJson(config[field]) !== stableJson(originalConfig[field])) {
          dirty.add(tab);
        }
      },
    );
    return dirty;
  }, [config, originalConfig]);

  const isDirty = dirtyTabs.size > 0;
  const hasJsonError = Object.values(jsonErrors).some((v) => v != null);

  const handleSave = async () => {
    if (!config || !orgId) return;
    if (!syncJsonFieldsIntoConfig()) {
      setSaveError("Fix the JSON errors before saving");
      return;
    }
    if (hasJsonError) {
      setSaveError("Fix the JSON errors before saving");
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await patchOrgConfigAdmin({
        path: { org_id: orgId },
        body: {
          metadata: config.metadata,
          notify: config.notify,
          templates: config.templates,
          tracking_number_seq: config.tracking_number_seq,
          incident_number_seq: config.incident_number_seq ?? null,
          export_configuration: config.export_configuration ?? null,
          ivr_inbound_number: config.ivr_inbound_number ?? null,
          response_notes: config.response_notes ?? null,
          // supported_languages / rejection_reasons are typed as `Object` in the
          // OpenAPI schema (via utoipa value_type) but are JSON arrays at
          // runtime — cast through unknown until the schema is loosened.
          supported_languages: (config.supported_languages ?? null) as {
            [key: string]: unknown;
          } | null,
          citizen_engage_general_reporting_enabled:
            config.citizen_engage_general_reporting_enabled ?? false,
          rejection_reasons: (config.rejection_reasons ?? null) as {
            [key: string]: unknown;
          } | null,
        },
      });
      const updated = response.data!;
      setConfig(updated);
      setOriginalConfig(updated);
      setFieldConfigurationsJson(
        formatJson(updated.metadata.field_configurations),
      );
      setMetadataJson(formatJson(updated.metadata));
      setSaveSuccess(true);
    } catch (err) {
      console.error(err);
      setSaveError(
        err instanceof Error ? err.message : "Failed to save org config",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (!originalConfig) return;
    setConfig(originalConfig);
    setFieldConfigurationsJson(
      formatJson(originalConfig.metadata.field_configurations),
    );
    setMetadataJson(formatJson(originalConfig.metadata));
    setJsonErrors({});
  };

  if (loadError) {
    return (
      <DevPage
        title="rv2_org_config"
        breadcrumbs={
          <Breadcrumbs>
            <Crumb>
              <Link to="/">Home</Link>
            </Crumb>
            <Crumb>
              <Link to="/dev_admin">Developer and Administrator Utilities</Link>
            </Crumb>
            <Crumb>
              <Link to="/dev_admin/org_config">Org Config</Link>
            </Crumb>
            <Crumb>{orgId}</Crumb>
          </Breadcrumbs>
        }
      >
        <Alert severity="error">{loadError}</Alert>
      </DevPage>
    );
  }

  if (!config) {
    return (
      <DevPage
        title="rv2_org_config"
        breadcrumbs={
          <Breadcrumbs>
            <Crumb>
              <Link to="/">Home</Link>
            </Crumb>
            <Crumb>
              <Link to="/dev_admin">Developer and Administrator Utilities</Link>
            </Crumb>
            <Crumb>
              <Link to="/dev_admin/org_config">Org Config</Link>
            </Crumb>
            <Crumb>{orgId}</Crumb>
          </Breadcrumbs>
        }
      >
        <Typography>Loading…</Typography>
      </DevPage>
    );
  }

  const meta = config.metadata;
  // The regex is evaluated in the portal's incident number field, so surface a
  // bad one here rather than letting it be silently ignored at validation time.
  const incidentNumberRegexError = (() => {
    const raw = meta.incident_number_regex?.trim();
    if (!raw) return null;
    try {
      new RegExp(raw);
      return null;
    } catch (e) {
      return `Invalid regex: ${(e as Error).message}`;
    }
  })();
  // Export halves are independent and each may be absent; the switches on the
  // Exports tab create/clear them, and these helpers patch within one half.
  const rmsExport = config.export_configuration?.rms_export_configuration;
  const demsExport = config.export_configuration?.dems_export_configuration;
  // Defaulted for reads: a row can already name the mark43 provider with no
  // nested config yet. Writes go through patchMark43, which creates it.
  const mark43 = rmsExport?.mark43_configuration ?? { base_url: "" };
  // Turning both halves off clears the whole column rather than leaving
  // {rms: null, dems: null}. The backend treats those the same, but "not set"
  // should read as not set.
  const setExportConfig = (
    patch: Partial<NonNullable<typeof config.export_configuration>>,
  ) => {
    const merged = { ...(config.export_configuration ?? {}), ...patch };
    const empty =
      !merged.rms_export_configuration && !merged.dems_export_configuration;
    updateColumn("export_configuration", empty ? null : merged);
  };
  const patchRms = (patch: Partial<NonNullable<typeof rmsExport>>) =>
    setExportConfig({ rms_export_configuration: { ...rmsExport!, ...patch } });
  const patchDems = (patch: Partial<NonNullable<typeof demsExport>>) =>
    setExportConfig({
      dems_export_configuration: { ...demsExport!, ...patch },
    });
  const patchMark43 = (
    patch: Partial<
      NonNullable<NonNullable<typeof rmsExport>["mark43_configuration"]>
    >,
  ) =>
    patchRms({
      mark43_configuration: {
        base_url: "",
        ...rmsExport?.mark43_configuration,
        ...patch,
      },
    });
  const patchAxon = (
    patch: Partial<
      NonNullable<NonNullable<typeof demsExport>["axon_configuration"]>
    >,
  ) =>
    patchDems({
      axon_configuration: { ...demsExport!.axon_configuration!, ...patch },
    });

  return (
    <DevPage
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <Link to="/">Home</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin">Developer and Administrator Utilities</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin/org_config">Org Config</Link>
          </Crumb>
          <Crumb>{orgId}</Crumb>
        </Breadcrumbs>
      }
    >
      <Paper className="dev-org-config">
        <Box sx={{ mb: 2 }}>
          <Button
            onClick={() => navigate("/dev_admin/org_config")}
            startIcon={<ArrowBack />}
          >
            Back to org list
          </Button>
        </Box>

        <Typography variant="h4" gutterBottom>
          rv2_org_config
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          rubi_org_id: {config.rubi_org_id}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          time_created: {config.time_created} · time_updated:{" "}
          {config.time_updated}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            mt: 3,
            alignItems: "flex-start",
          }}
        >
          <ConfigSectionNav
            groups={TAB_GROUPS}
            labels={TAB_LABEL}
            active={activeTab}
            dirty={dirtyTabs}
            onSelect={setActiveTab}
            fieldIndex={FIELD_INDEX}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {activeTab === "general" && (
              <TabSection>
                <TabIntro tab="general" />
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      data-field="city"
                      fullWidth
                      label="City"
                      value={meta.city}
                      onChange={(e) => updateMetadata({ city: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      data-field="timezone"
                      fullWidth
                      label="Timezone"
                      value={meta.timezone ?? ""}
                      onChange={(e) =>
                        updateMetadata({ timezone: e.target.value || null })
                      }
                      helperText="IANA timezone, e.g. America/Toronto"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      data-field="address"
                      fullWidth
                      multiline
                      minRows={2}
                      label="Address"
                      value={meta.address ?? ""}
                      onChange={(e) =>
                        updateMetadata({ address: e.target.value || null })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      data-field="contact_email"
                      fullWidth
                      label="Contact email"
                      value={meta.contact_email}
                      onChange={(e) =>
                        updateMetadata({ contact_email: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      data-field="emergency_number"
                      fullWidth
                      label="Emergency number"
                      value={meta.emergency_number}
                      onChange={(e) =>
                        updateMetadata({ emergency_number: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      data-field="nonemergency_number"
                      fullWidth
                      label="Non-emergency number"
                      value={meta.nonemergency_number}
                      onChange={(e) =>
                        updateMetadata({ nonemergency_number: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      data-field="use_contact_email_for_responses"
                      control={
                        <Switch
                          checked={
                            meta.use_contact_email_for_responses ?? false
                          }
                          onChange={(e) =>
                            updateMetadata({
                              use_contact_email_for_responses: e.target.checked,
                            })
                          }
                        />
                      }
                      label={
                        <LabelWithInfo
                          label="Use contact email for responses"
                          helpKey="use_contact_email_for_responses"
                        />
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      data-field="cc_responding_user"
                      control={
                        <Switch
                          checked={meta.cc_responding_user ?? false}
                          onChange={(e) =>
                            updateMetadata({
                              cc_responding_user: e.target.checked,
                            })
                          }
                        />
                      }
                      label={
                        <LabelWithInfo
                          label="CC responding user"
                          helpKey="cc_responding_user"
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </TabSection>
            )}

            {activeTab === "urls" && (
              <TabSection>
                <TabIntro tab="urls" />
                <Grid container spacing={3}>
                  {(
                    [
                      ["frontend_url", "Frontend URL"],
                      ["homepage_url", "Homepage URL"],
                      ["camera_registry_url", "Camera registry URL"],
                      ["online_reporting_url", "Online reporting URL"],
                      ["registries_url", "Registries URL"],
                      [
                        "supplemental_reporting_url",
                        "Supplemental reporting URL",
                      ],
                      ["logo_url", "Logo URL"],
                    ] as const
                  ).map(([field, label]) => (
                    <Grid size={{ xs: 12 }} key={field}>
                      <TextField
                        data-field={field}
                        fullWidth
                        label={label}
                        value={(meta[field] as string | null | undefined) ?? ""}
                        onChange={(e) =>
                          updateMetadata({
                            [field]: e.target.value || null,
                          } as Partial<OrgConfigMetadata>)
                        }
                        InputProps={helpAdornment(field)}
                      />
                    </Grid>
                  ))}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      data-field="alternative_supplemental_direction"
                      fullWidth
                      multiline
                      minRows={2}
                      label="Alternative supplemental direction"
                      value={meta.alternative_supplemental_direction ?? ""}
                      onChange={(e) =>
                        updateMetadata({
                          alternative_supplemental_direction:
                            e.target.value || null,
                        })
                      }
                      helperText="Free-form direction shown when no supplemental URL is set"
                      InputProps={helpAdornment(
                        "alternative_supplemental_direction",
                      )}
                    />
                  </Grid>
                </Grid>
              </TabSection>
            )}

            {activeTab === "identifiers" && (
              <TabSection>
                <TabIntro tab="identifiers" />
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      data-field="tracking_number_prefix"
                      fullWidth
                      label="Tracking number prefix"
                      value={meta.tracking_number_prefix}
                      onChange={(e) =>
                        updateMetadata({
                          tracking_number_prefix: e.target.value,
                        })
                      }
                      InputProps={helpAdornment("tracking_number_prefix")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      data-field="ori_number"
                      fullWidth
                      label="ORI number"
                      value={meta.ori_number ?? ""}
                      onChange={(e) =>
                        updateMetadata({ ori_number: e.target.value || null })
                      }
                      InputProps={helpAdornment("ori_number")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      data-field="agency_code"
                      fullWidth
                      label="Agency code"
                      value={meta.agency_code ?? ""}
                      onChange={(e) =>
                        updateMetadata({ agency_code: e.target.value || null })
                      }
                      InputProps={helpAdornment("agency_code")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      data-field="automated_incident_number_generation"
                      control={
                        <Switch
                          checked={
                            meta.automated_incident_number_generation ?? false
                          }
                          onChange={(e) =>
                            updateMetadata({
                              automated_incident_number_generation:
                                e.target.checked,
                            })
                          }
                        />
                      }
                      label={
                        <LabelWithInfo
                          label="Automated incident number generation"
                          helpKey="automated_incident_number_generation"
                        />
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      data-field="incident_number_pattern"
                      fullWidth
                      label="Incident number pattern"
                      value={meta.incident_number_pattern ?? ""}
                      onChange={(e) =>
                        updateMetadata({
                          incident_number_pattern: e.target.value || null,
                        })
                      }
                      helperText="E.g. {yyyy}-{seq}"
                      InputProps={helpAdornment("incident_number_pattern")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      data-field="incident_number_length"
                      fullWidth
                      type="number"
                      label="Incident number length"
                      value={meta.incident_number_length ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        updateMetadata({
                          incident_number_length:
                            v === "" ? null : Number.parseInt(v, 10),
                        });
                      }}
                      InputProps={helpAdornment("incident_number_length")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      data-field="incident_number_regex"
                      fullWidth
                      label="Incident number regex (accepted values)"
                      value={meta.incident_number_regex ?? ""}
                      onChange={(e) =>
                        updateMetadata({
                          incident_number_regex: e.target.value || null,
                        })
                      }
                      error={!!incidentNumberRegexError}
                      helperText={
                        incidentNumberRegexError ??
                        "E.g. \\d{4}-\\d{6}. Blank falls back to the pattern/length above."
                      }
                      InputProps={helpAdornment("incident_number_regex")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ChipListField
                      anchor="incident_generation_outcomes"
                      label="Incident generation outcomes"
                      value={meta.incident_generation_outcomes}
                      onChange={(v) =>
                        updateMetadata({ incident_generation_outcomes: v })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ChipListField
                      anchor="rms_export_outcomes"
                      label="RMS export outcomes"
                      value={meta.rms_export_outcomes}
                      onChange={(v) =>
                        updateMetadata({ rms_export_outcomes: v })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ChipListField
                      anchor="dems_export_outcomes"
                      label="DEMS export outcomes"
                      value={meta.dems_export_outcomes}
                      onChange={(v) =>
                        updateMetadata({ dems_export_outcomes: v })
                      }
                    />
                  </Grid>
                </Grid>
              </TabSection>
            )}

            {activeTab === "behavior" && (
              <TabSection>
                <TabIntro tab="behavior" />
                <Stack spacing={2}>
                  {(
                    [
                      [
                        "include_logo_in_email_templates",
                        "Include logo in email templates",
                      ],
                      [
                        "include_pdf_in_notification_emails",
                        "Include PDF in notification emails",
                      ],
                      ["enable_crime_codes", "Enable crime codes"],
                      ["display_address_on_pdf", "Display address on PDF"],
                      [
                        "include_disclaimer_header_on_pdf",
                        "Include disclaimer header on PDF",
                      ],
                      [
                        "require_synopsis_verification",
                        "Require synopsis verification",
                      ],
                      [
                        "perform_attachment_conversion_to_pdf",
                        "Perform attachment conversion to PDF",
                      ],
                      [
                        "require_internal_user_id",
                        "Require an Internal User ID on user profiles",
                      ],
                    ] as const
                  ).map(([field, label]) => (
                    <FormControlLabel
                      key={field}
                      data-field={field}
                      control={
                        <Switch
                          checked={(meta[field] as boolean | null) ?? false}
                          onChange={(e) =>
                            updateMetadata({
                              [field]: e.target.checked,
                            } as Partial<OrgConfigMetadata>)
                          }
                        />
                      }
                      label={<LabelWithInfo label={label} helpKey={field} />}
                    />
                  ))}

                  <TextField
                    data-field="rms_export_model"
                    fullWidth
                    label="RMS export model"
                    value={meta.rms_export_model ?? ""}
                    onChange={(e) =>
                      updateMetadata({
                        rms_export_model: e.target.value || null,
                      })
                    }
                    InputProps={helpAdornment("rms_export_model")}
                  />
                  <TextField
                    data-field="rms_export_badge_number_override"
                    fullWidth
                    label="RMS export badge number override"
                    value={meta.rms_export_badge_number_override ?? ""}
                    onChange={(e) =>
                      updateMetadata({
                        rms_export_badge_number_override:
                          e.target.value || null,
                      })
                    }
                    InputProps={helpAdornment(
                      "rms_export_badge_number_override",
                    )}
                  />
                  <ChipListField
                    anchor="tag_options"
                    label="Tag options"
                    value={meta.tag_options}
                    onChange={(v) => updateMetadata({ tag_options: v ?? [] })}
                  />
                  <PairListField
                    anchor="priority_options"
                    label="Priority options"
                    value={meta.priority_options ?? null}
                    onChange={(v) => updateMetadata({ priority_options: v })}
                    keyProp="name"
                    valueProp="value"
                    keyLabel="Name"
                    valueLabel="Value"
                    numericValue
                    helperText="Shown in the priority picker; the value orders them."
                  />
                  <FieldGroup title="Reports & attachments">
                    <BoolField
                      anchor="use_survey_group_ui"
                      label="Use survey-group UI"
                      value={meta.use_survey_group_ui}
                      onChange={(v) =>
                        updateMetadata({ use_survey_group_ui: v })
                      }
                    />
                    <BoolField
                      anchor="enable_attachment_title"
                      label="Enable attachment titles"
                      value={meta.enable_attachment_title}
                      onChange={(v) =>
                        updateMetadata({ enable_attachment_title: v })
                      }
                    />
                    <BoolField
                      anchor="generate_attachment_descriptions"
                      label="Generate attachment descriptions"
                      value={meta.generate_attachment_descriptions}
                      onChange={(v) =>
                        updateMetadata({ generate_attachment_descriptions: v })
                      }
                      helperText="Uses the model to describe uploaded attachments."
                    />
                    <BoolField
                      anchor="disable_modification_request"
                      label="Disable modification requests"
                      value={meta.disable_modification_request}
                      onChange={(v) =>
                        updateMetadata({ disable_modification_request: v })
                      }
                      helperText="Hides the citizen 'request a change' flow."
                    />
                    <TextField
                      data-field="attachment_type_item_type_option_id"
                      fullWidth
                      label="Attachment type item-type-option id"
                      value={meta.attachment_type_item_type_option_id ?? ""}
                      onChange={(e) =>
                        updateMetadata({
                          attachment_type_item_type_option_id:
                            e.target.value.trim() || null,
                        })
                      }
                      helperText="UUID of the dropdown list backing attachment types."
                    />
                  </FieldGroup>
                </Stack>
              </TabSection>
            )}

            {activeTab === "notify" && (
              <TabSection anchor="notify">
                <TabIntro tab="notify" />
                <Stack spacing={4}>
                  <FieldGroup title="General recipient lists">
                    <NotifyItemsField
                      value={config.notify?.notify_items ?? []}
                      onChange={(items) =>
                        updateColumn("notify", {
                          ...(config.notify ?? { notify_items: [] }),
                          notify_items: items,
                        })
                      }
                      keyLabel="Report class key"
                      keyHelper='Survey class slug, or "all" to match every report.'
                    />
                  </FieldGroup>
                  <FieldGroup title="Status recipient lists">
                    <NotifyItemsField
                      value={config.notify?.status_notify_items ?? []}
                      onChange={(items) =>
                        updateColumn("notify", {
                          ...(config.notify ?? { notify_items: [] }),
                          status_notify_items: items.length ? items : null,
                        })
                      }
                      keyLabel="Key"
                      addLabel="Add status recipient list"
                      emptyText="No status recipient lists."
                    />
                  </FieldGroup>
                  <FieldGroup title="Per-status recipient lists">
                    <StatusNotifyGroupsField
                      value={
                        config.notify?.per_report_status_notify_items ?? []
                      }
                      onChange={(groups) =>
                        updateColumn("notify", {
                          ...(config.notify ?? { notify_items: [] }),
                          per_report_status_notify_items: groups,
                        })
                      }
                    />
                  </FieldGroup>
                </Stack>
              </TabSection>
            )}

            {activeTab === "templates" && (
              <TabSection anchor="templates">
                <TabIntro tab="templates" />
                <TemplateListField
                  value={config.templates?.templates ?? []}
                  onChange={(templates) =>
                    updateColumn("templates", { templates })
                  }
                />
              </TabSection>
            )}

            {activeTab === "exports" && (
              <TabSection anchor="export_configuration">
                <TabIntro tab="exports" />
                <Stack spacing={4}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            !!config.export_configuration
                              ?.rms_export_configuration
                          }
                          onChange={(e) =>
                            setExportConfig({
                              rms_export_configuration: e.target.checked
                                ? (config.export_configuration
                                    ?.rms_export_configuration ?? {
                                    provider: "",
                                    export_model: "",
                                    include_images: false,
                                    include_other_media: false,
                                    include_documents: false,
                                    include_record_pdf: false,
                                  })
                                : null,
                            })
                          }
                        />
                      }
                      label={
                        <Typography variant="subtitle2">RMS export</Typography>
                      }
                    />
                    {rmsExport && (
                      <Stack spacing={2} sx={{ mt: 1, pl: 6 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <Box sx={{ flex: 1 }}>
                            <SuggestField
                              label="Provider"
                              value={rmsExport.provider}
                              onChange={(v) =>
                                patchRms({
                                  provider: v,
                                  // Selecting Mark43 reveals its settings below,
                                  // so give them something to bind to. Switching
                                  // away leaves the config in place rather than
                                  // discarding what was typed — the backend only
                                  // reads it when the provider matches.
                                  ...(isMark43Provider(v) &&
                                  !rmsExport.mark43_configuration
                                    ? { mark43_configuration: { base_url: "" } }
                                    : {}),
                                })
                              }
                              options={RMS_PROVIDERS}
                              required
                              helperText="Mark43 is push-based: an export is queued at approval. Every other provider is pull-based, polled by an on-prem agent."
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <SuggestField
                              label="Export model"
                              value={rmsExport.export_model}
                              onChange={(v) => patchRms({ export_model: v })}
                              options={RMS_EXPORT_MODELS}
                              required
                              helperText="Note: metadata.rms_export_model on the Behaviour tab is a separate value."
                            />
                          </Box>
                        </Stack>
                        <FieldGroup title="What to include">
                          <BoolField
                            anchor="include_images"
                            label="Images"
                            value={rmsExport.include_images}
                            onChange={(v) => patchRms({ include_images: v })}
                          />
                          <BoolField
                            anchor="include_other_media"
                            label="Other media"
                            value={rmsExport.include_other_media}
                            onChange={(v) =>
                              patchRms({ include_other_media: v })
                            }
                          />
                          <BoolField
                            anchor="include_documents"
                            label="Documents"
                            value={rmsExport.include_documents}
                            onChange={(v) => patchRms({ include_documents: v })}
                          />
                          <BoolField
                            anchor="include_pdf_documents"
                            label="PDF documents"
                            value={rmsExport.include_pdf_documents}
                            onChange={(v) =>
                              patchRms({ include_pdf_documents: v })
                            }
                          />
                          <BoolField
                            anchor="include_record_pdf"
                            label="Record PDF"
                            value={rmsExport.include_record_pdf}
                            onChange={(v) =>
                              patchRms({ include_record_pdf: v })
                            }
                          />
                          <BoolField
                            anchor="include_internal_notes"
                            label="Internal notes"
                            value={rmsExport.include_internal_notes}
                            onChange={(v) =>
                              patchRms({ include_internal_notes: v })
                            }
                          />
                          <BoolField
                            anchor="prefer_pdf_converted_documents"
                            label="Prefer PDF-converted documents"
                            value={rmsExport.prefer_pdf_converted_documents}
                            onChange={(v) =>
                              patchRms({ prefer_pdf_converted_documents: v })
                            }
                          />
                        </FieldGroup>
                        <BoolField
                          anchor="use_user_unit_for_ori"
                          label="Use the user's unit for the ORI"
                          value={rmsExport.use_user_unit_for_ori}
                          onChange={(v) =>
                            patchRms({ use_user_unit_for_ori: v })
                          }
                          helperText="Overrides the agency-level ORI on the Identifiers tab."
                        />
                        <BoolField
                          anchor="use_internal_user_id_for_badge"
                          label="Use the user's internal ID as the badge"
                          value={rmsExport.use_internal_user_id_for_badge}
                          onChange={(v) =>
                            patchRms({ use_internal_user_id_for_badge: v })
                          }
                          helperText="Exports the processing officer's Internal User ID in place of their badge number. Users without one fall back to their badge."
                        />
                        <TextField
                          data-field="export_batch_count"
                          label="Export batch count"
                          type="number"
                          size="small"
                          sx={{ maxWidth: 260 }}
                          value={rmsExport.export_batch_count ?? ""}
                          onChange={(e) =>
                            patchRms({
                              export_batch_count:
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                            })
                          }
                          helperText="Records per machine-to-machine download. Defaults to 50 when unset; 0 or less removes the limit."
                        />

                        {isMark43Provider(rmsExport.provider) && (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Mark43 configuration
                            </Typography>
                            <Stack spacing={2} sx={{ mt: 1, pl: 2 }}>
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Base URL"
                                value={mark43.base_url}
                                onChange={(e) =>
                                  patchMark43({ base_url: e.target.value })
                                }
                                error={!mark43.base_url}
                                helperText="Full v2 URL, e.g. https://albuquerque.api.us-gov-aws-1.mark43.com/v2 — not the /partnerships/api v1 host."
                              />

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Agency — at least one identifier is required.
                              </Typography>
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Agency ORI"
                                value={mark43.agency_ori ?? ""}
                                onChange={(e) =>
                                  patchMark43({
                                    agency_ori: e.target.value || null,
                                  })
                                }
                                helperText="The department's FBI ORI. Mark43 resolves its agency profile from this."
                              />
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Agency Mark43 ID"
                                type="number"
                                value={mark43.agency_mark43_id ?? ""}
                                onChange={(e) =>
                                  patchMark43({
                                    agency_mark43_id:
                                      e.target.value === ""
                                        ? null
                                        : Number(e.target.value),
                                  })
                                }
                              />
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Agency code"
                                value={mark43.agency_code ?? ""}
                                onChange={(e) =>
                                  patchMark43({
                                    agency_code: e.target.value || null,
                                  })
                                }
                              />

                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                select
                                label="Report endpoint"
                                value={mark43.report_endpoint ?? "offense"}
                                onChange={(e) =>
                                  patchMark43({
                                    report_endpoint: e.target
                                      .value as Mark43ReportEndpoint,
                                  })
                                }
                                helperText="Offense uses Mark43's built-in incident/offense type and needs no department setup. Custom requires a report type the department has configured for us."
                              >
                                <MenuItem value="offense">
                                  offense (recommended)
                                </MenuItem>
                                <MenuItem value="custom">custom</MenuItem>
                              </TextField>
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                select
                                label="Approval status on create"
                                value={mark43.approval_status ?? "SUBMITTED"}
                                onChange={(e) =>
                                  patchMark43({
                                    approval_status: e.target
                                      .value as Mark43ApprovalStatus,
                                  })
                                }
                                helperText="Anything but DRAFT requires a submitting user below."
                              >
                                <MenuItem value="DRAFT">DRAFT</MenuItem>
                                <MenuItem value="SUBMITTED">SUBMITTED</MenuItem>
                                <MenuItem value="APPROVED">APPROVED</MenuItem>
                              </TextField>
                              <KeyedMapField
                                label="Report types by survey class"
                                anchor="mark43_report_types"
                                value={
                                  (mark43.report_types ?? null) as Record<
                                    string,
                                    string
                                  > | null
                                }
                                onChange={(v) =>
                                  patchMark43({ report_types: v })
                                }
                                multiValue={false}
                                keyLabel="Survey class"
                                valueLabel="Report type"
                                helperText={
                                  'Only used by the custom endpoint. Use "all" as the fallback key.'
                                }
                              />

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Submitting user — Mark43 requires one unless
                                reports land as DRAFT.
                              </Typography>
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Submitting user Mark43 ID"
                                type="number"
                                value={mark43.submitting_user_mark43_id ?? ""}
                                onChange={(e) =>
                                  patchMark43({
                                    submitting_user_mark43_id:
                                      e.target.value === ""
                                        ? null
                                        : Number(e.target.value),
                                  })
                                }
                                error={
                                  (mark43.approval_status ?? "SUBMITTED") !==
                                    "DRAFT" &&
                                  !mark43.submitting_user_mark43_id &&
                                  !mark43.submitting_user_badge_number
                                }
                                helperText="A service account rather than a reviewing officer — a citizen report has no Mark43 officer behind it."
                              />
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Submitting user badge number"
                                value={
                                  mark43.submitting_user_badge_number ?? ""
                                }
                                onChange={(e) =>
                                  patchMark43({
                                    submitting_user_badge_number:
                                      e.target.value || null,
                                  })
                                }
                                helperText="Alternative to the ID above."
                              />

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Reporting event number
                              </Typography>
                              <BoolField
                                anchor="send_own_reporting_event_number"
                                label="Send Rubicon's incident number"
                                value={mark43.send_own_reporting_event_number}
                                onChange={(v) =>
                                  patchMark43({
                                    send_own_reporting_event_number: v,
                                  })
                                }
                                helperText="On by default. Off lets Mark43 assign its own, which leaves the reporting party holding a number the RMS does not have. The incident number pattern must not collide with the department's own numbering."
                              />
                              <BoolField
                                anchor="ignore_ren_format_validation"
                                label="Skip Mark43's REN format check"
                                value={mark43.ignore_ren_format_validation}
                                onChange={(v) =>
                                  patchMark43({
                                    ignore_ren_format_validation: v,
                                  })
                                }
                                helperText="Usually needed when sending our own number, since it will not match the department's format."
                              />

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Attachments
                              </Typography>
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Attachment batch size"
                                type="number"
                                value={mark43.attachment_batch_size ?? ""}
                                onChange={(e) =>
                                  patchMark43({
                                    attachment_batch_size:
                                      e.target.value === ""
                                        ? null
                                        : Number(e.target.value),
                                  })
                                }
                                helperText="Files per upload request. Defaults to 5."
                              />
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Attachment batch size (MB)"
                                type="number"
                                value={mark43.attachment_batch_size_mb ?? ""}
                                onChange={(e) =>
                                  patchMark43({
                                    attachment_batch_size_mb:
                                      e.target.value === ""
                                        ? null
                                        : Number(e.target.value),
                                  })
                                }
                                helperText="Byte ceiling per upload request. Defaults to 25."
                              />

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Supplementals
                              </Typography>
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Supplement report type"
                                value={mark43.supplement_report_type ?? ""}
                                onChange={(e) =>
                                  patchMark43({
                                    supplement_report_type:
                                      e.target.value || null,
                                  })
                                }
                                helperText={
                                  'Required for supplementals — "Supplement" in Albuquerque. Unlike the main report, Mark43 will not assign one.'
                                }
                              />
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Supplement report definition ID"
                                type="number"
                                value={
                                  mark43.supplement_report_definition_id ?? ""
                                }
                                onChange={(e) =>
                                  patchMark43({
                                    supplement_report_definition_id:
                                      e.target.value === ""
                                        ? null
                                        : Number(e.target.value),
                                  })
                                }
                                helperText="Alternative to the type name above."
                              />
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Supplement type abbreviation"
                                value={
                                  mark43.supplement_type_abbreviation ?? ""
                                }
                                onChange={(e) =>
                                  patchMark43({
                                    supplement_type_abbreviation:
                                      e.target.value || null,
                                  })
                                }
                                helperText={
                                  'A SUPPLEMENT_TYPE attribute code, e.g. "MPAI" for Narrative Supplement.'
                                }
                              />
                              <TextField
                                data-field="mark43_configuration"
                                size="small"
                                label="Supplement type Mark43 ID"
                                type="number"
                                value={mark43.supplement_type_mark43_id ?? ""}
                                onChange={(e) =>
                                  patchMark43({
                                    supplement_type_mark43_id:
                                      e.target.value === ""
                                        ? null
                                        : Number(e.target.value),
                                  })
                                }
                                helperText="Alternative to the abbreviation above."
                              />

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Backlink to the Rubicon record
                              </Typography>
                              <BoolField
                                anchor="include_external_link"
                                label="Send a report external link"
                                value={mark43.include_external_link}
                                onChange={(v) =>
                                  patchMark43({ include_external_link: v })
                                }
                                helperText="Carries Rubicon's tracking number into Mark43, where it stays searchable. Both attribute codes below are required — the link is omitted without them."
                              />
                              {mark43.include_external_link && (
                                <>
                                  <TextField
                                    data-field="mark43_configuration"
                                    size="small"
                                    label="Source system abbreviation"
                                    value={
                                      mark43.external_link_source_system_abbreviation ??
                                      ""
                                    }
                                    onChange={(e) =>
                                      patchMark43({
                                        external_link_source_system_abbreviation:
                                          e.target.value || null,
                                      })
                                    }
                                    error={
                                      !mark43.external_link_source_system_abbreviation
                                    }
                                    helperText={
                                      'An EXTERNAL_RECORD_SOURCE code — "OT" (Other) in Albuquerque, since that list has no value for another software system.'
                                    }
                                  />
                                  <TextField
                                    data-field="mark43_configuration"
                                    size="small"
                                    label="Reason for association abbreviation"
                                    value={
                                      mark43.external_link_reason_abbreviation ??
                                      ""
                                    }
                                    onChange={(e) =>
                                      patchMark43({
                                        external_link_reason_abbreviation:
                                          e.target.value || null,
                                      })
                                    }
                                    error={
                                      !mark43.external_link_reason_abbreviation
                                    }
                                    helperText={
                                      'A REASON_FOR_ASSOCIATION code — "SI" (Same Incident) in Albuquerque.'
                                    }
                                  />
                                </>
                              )}

                              <Alert severity="info">
                                The API credential is read from the
                                deployment&apos;s{" "}
                                <code>
                                  mark43_export_credentials/&lt;org id&gt;
                                </code>{" "}
                                SSM parameter, not from this page — and only at
                                startup, so a newly added agency needs an API
                                restart. Set the org&apos;s timezone on the
                                Metadata tab too: incident times resolve against
                                it, and the fallback is America/Toronto.
                              </Alert>
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    )}
                  </Box>

                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            !!config.export_configuration
                              ?.dems_export_configuration
                          }
                          onChange={(e) =>
                            setExportConfig({
                              dems_export_configuration: e.target.checked
                                ? (config.export_configuration
                                    ?.dems_export_configuration ?? {
                                    provider: "evidence.com",
                                    include_images: false,
                                    include_other_media: false,
                                    include_documents: false,
                                  })
                                : null,
                            })
                          }
                        />
                      }
                      label={
                        <Typography variant="subtitle2">
                          DEMS / evidence export
                        </Typography>
                      }
                    />
                    {demsExport && (
                      <Stack spacing={2} sx={{ mt: 1, pl: 6 }}>
                        <Box sx={{ maxWidth: 320 }}>
                          <SuggestField
                            label="Provider"
                            value={demsExport.provider}
                            onChange={(v) => patchDems({ provider: v })}
                            options={DEMS_PROVIDERS}
                            required
                          />
                        </Box>
                        <FieldGroup title="What to include">
                          <BoolField
                            anchor="include_images"
                            label="Images"
                            value={demsExport.include_images}
                            onChange={(v) => patchDems({ include_images: v })}
                          />
                          <BoolField
                            anchor="include_other_media"
                            label="Other media"
                            value={demsExport.include_other_media}
                            onChange={(v) =>
                              patchDems({ include_other_media: v })
                            }
                          />
                          <BoolField
                            anchor="include_documents"
                            label="Documents"
                            value={demsExport.include_documents}
                            onChange={(v) =>
                              patchDems({ include_documents: v })
                            }
                          />
                          <BoolField
                            anchor="include_pdf_documents"
                            label="PDF documents"
                            value={demsExport.include_pdf_documents}
                            onChange={(v) =>
                              patchDems({ include_pdf_documents: v })
                            }
                            helperText="Defaults to on when unset."
                          />
                        </FieldGroup>
                        <BoolField
                          anchor="include_evidence_summary_in_synopsis"
                          label="Include an evidence summary in the synopsis"
                          value={
                            demsExport.include_evidence_summary_in_synopsis
                          }
                          onChange={(v) =>
                            patchDems({
                              include_evidence_summary_in_synopsis: v,
                            })
                          }
                        />
                        <BoolField
                          anchor="create_cases"
                          label="Create cases"
                          value={demsExport.create_cases}
                          onChange={(v) => patchDems({ create_cases: v })}
                        />
                        <TextField
                          data-field="badge_number_override"
                          label="Badge number override"
                          size="small"
                          sx={{ maxWidth: 320 }}
                          value={demsExport.badge_number_override ?? ""}
                          onChange={(e) =>
                            patchDems({
                              badge_number_override:
                                e.target.value.trim() || null,
                            })
                          }
                        />
                        <TextField
                          data-field="file_number_prefix"
                          label="File number prefix"
                          size="small"
                          sx={{ maxWidth: 320 }}
                          value={demsExport.file_number_prefix ?? ""}
                          onChange={(e) =>
                            patchDems({
                              file_number_prefix: e.target.value.trim() || null,
                            })
                          }
                          helperText='Prepended to the incident number in the evidence ID and titles, e.g. "VI-".'
                        />
                        <KeyedMapField
                          anchor="categories"
                          label="Evidence categories"
                          multiValue
                          value={
                            (demsExport.categories ?? null) as Record<
                              string,
                              string[]
                            > | null
                          }
                          onChange={(v) => patchDems({ categories: v })}
                          valueLabel="Category names"
                          helperText='Categories tagged onto exported evidence. An "all" row is merged with the matching survey class.'
                        />
                        <KeyedMapField
                          anchor="evidence_group"
                          label="Evidence group"
                          multiValue={false}
                          value={
                            (demsExport.evidence_group ?? null) as Record<
                              string,
                              string
                            > | null
                          }
                          onChange={(v) => patchDems({ evidence_group: v })}
                          valueLabel="Evidence group ID"
                          helperText='Group the evidence is assigned to. A survey-class row wins over "all".'
                        />
                        <Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!demsExport.axon_configuration}
                                onChange={(e) =>
                                  patchDems({
                                    axon_configuration: e.target.checked
                                      ? (demsExport.axon_configuration ?? {
                                          agency_uri: "",
                                          client_id: "",
                                          agency_id: "",
                                        })
                                      : null,
                                  })
                                }
                              />
                            }
                            label={
                              <Typography variant="body2">
                                Axon configuration
                              </Typography>
                            }
                          />
                          {demsExport.axon_configuration && (
                            <Stack spacing={2} sx={{ mt: 1, pl: 6 }}>
                              <TextField
                                data-field="axon_configuration"
                                size="small"
                                label="Agency URI"
                                value={demsExport.axon_configuration.agency_uri}
                                onChange={(e) =>
                                  patchAxon({ agency_uri: e.target.value })
                                }
                                error={
                                  !demsExport.axon_configuration.agency_uri
                                }
                              />
                              <TextField
                                data-field="axon_configuration"
                                size="small"
                                label="Client ID"
                                value={demsExport.axon_configuration.client_id}
                                onChange={(e) =>
                                  patchAxon({ client_id: e.target.value })
                                }
                                error={!demsExport.axon_configuration.client_id}
                              />
                              <TextField
                                data-field="axon_configuration"
                                size="small"
                                label="Agency ID"
                                value={demsExport.axon_configuration.agency_id}
                                onChange={(e) =>
                                  patchAxon({ agency_id: e.target.value })
                                }
                                error={!demsExport.axon_configuration.agency_id}
                              />
                              <Alert severity="info">
                                The matching client secret is read from the
                                deployment&apos;s{" "}
                                <code>axon_evidence_credentials</code>{" "}
                                parameter, not from this config. Nothing secret
                                belongs on this page.
                              </Alert>
                            </Stack>
                          )}
                        </Box>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </TabSection>
            )}

            {activeTab === "columns" && (
              <TabSection>
                <TabIntro tab="columns" />
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      data-field="tracking_number_seq"
                      fullWidth
                      type="number"
                      label="tracking_number_seq"
                      value={config.tracking_number_seq}
                      onChange={(e) =>
                        updateColumn(
                          "tracking_number_seq",
                          Number.parseInt(e.target.value || "0", 10),
                        )
                      }
                      InputProps={helpAdornment("tracking_number_seq")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      data-field="incident_number_seq"
                      fullWidth
                      type="number"
                      label="incident_number_seq"
                      value={config.incident_number_seq ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        updateColumn(
                          "incident_number_seq",
                          v === "" ? null : Number.parseInt(v, 10),
                        );
                      }}
                      InputProps={helpAdornment("incident_number_seq")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      data-field="ivr_inbound_number"
                      fullWidth
                      label="ivr_inbound_number"
                      value={config.ivr_inbound_number ?? ""}
                      onChange={(e) =>
                        updateColumn(
                          "ivr_inbound_number",
                          e.target.value || null,
                        )
                      }
                      InputProps={helpAdornment("ivr_inbound_number")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      data-field="citizen_engage_general_reporting_enabled"
                      control={
                        <Switch
                          checked={
                            config.citizen_engage_general_reporting_enabled ??
                            false
                          }
                          onChange={(e) =>
                            updateColumn(
                              "citizen_engage_general_reporting_enabled",
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label={
                        <LabelWithInfo
                          label="citizen_engage_general_reporting_enabled"
                          helpKey="citizen_engage_general_reporting_enabled"
                        />
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ChipListField
                      anchor="supported_languages"
                      label="Supported languages"
                      value={asStringList(config.supported_languages)}
                      onChange={(v) => updateColumn("supported_languages", v)}
                      placeholder="Language code, e.g. en or fr"
                      helperText="Locale codes the citizen forms offer. Enter or comma to add."
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ChipListField
                      anchor="rejection_reasons"
                      label="Rejection reasons"
                      value={asStringList(config.rejection_reasons)}
                      onChange={(v) => updateColumn("rejection_reasons", v)}
                      placeholder="e.g. Duplicate"
                      helperText="Offered when a reviewer rejects a submission."
                    />
                  </Grid>
                </Grid>
              </TabSection>
            )}

            {activeTab === "citizen" && (
              <TabSection>
                <TabIntro tab="citizen" />
                <Stack spacing={3}>
                  <FieldGroup title="Links">
                    <TextField
                      data-field="collision_center_url"
                      fullWidth
                      label="Collision centre URL"
                      value={meta.collision_center_url ?? ""}
                      onChange={(e) =>
                        updateMetadata({
                          collision_center_url: e.target.value.trim() || null,
                        })
                      }
                      helperText="Where collision reporting is directed. Empty hides the link."
                    />
                    <TextField
                      data-field="temporary_online_reporting_override"
                      fullWidth
                      label="Temporary online-reporting override"
                      value={meta.temporary_online_reporting_override ?? ""}
                      onChange={(e) =>
                        updateMetadata({
                          temporary_online_reporting_override:
                            e.target.value.trim() || null,
                        })
                      }
                      helperText="Sends citizens elsewhere while Rubicon reporting is paused. Wins over the normal reporting URL wherever it is set."
                    />
                    <TitleUrlField
                      anchor="non_technical_issues_website"
                      label="Non-technical issues link"
                      value={meta.non_technical_issues_website ?? null}
                      onChange={(v) =>
                        updateMetadata({ non_technical_issues_website: v })
                      }
                      helperText="Shown for enquiries that aren't site problems. Both empty clears it."
                    />
                  </FieldGroup>
                  <FieldGroup title="Appearance & calls to action">
                    <ColourField
                      anchor="header_colour_override"
                      label="Header colour override"
                      value={meta.header_colour_override ?? null}
                      onChange={(v) =>
                        updateMetadata({ header_colour_override: v })
                      }
                    />
                    <BoolField
                      anchor="hide_landing_page_report_now_button"
                      label="Hide the landing-page 'Report now' button"
                      value={meta.hide_landing_page_report_now_button}
                      onChange={(v) =>
                        updateMetadata({
                          hide_landing_page_report_now_button: v,
                        })
                      }
                    />
                  </FieldGroup>
                  <FieldGroup title="Contact numbers to hide">
                    <TextField
                      data-field="citizen_engage_disable_hidden_contact_number"
                      fullWidth
                      label="Hidden contact number (citizen engage)"
                      value={
                        meta.citizen_engage_disable_hidden_contact_number ?? ""
                      }
                      onChange={(e) =>
                        updateMetadata({
                          citizen_engage_disable_hidden_contact_number:
                            e.target.value.trim() || null,
                        })
                      }
                    />
                    <TextField
                      data-field="citizen_engage_disable_reports_contact_number"
                      fullWidth
                      label="Reports contact number (citizen engage)"
                      value={
                        meta.citizen_engage_disable_reports_contact_number ?? ""
                      }
                      onChange={(e) =>
                        updateMetadata({
                          citizen_engage_disable_reports_contact_number:
                            e.target.value.trim() || null,
                        })
                      }
                    />
                  </FieldGroup>
                </Stack>
              </TabSection>
            )}

            {activeTab === "lists" && (
              <TabSection>
                <TabIntro tab="lists" />
                <Stack spacing={3}>
                  <PairListField
                    anchor="units"
                    label="Units"
                    value={meta.units ?? null}
                    onChange={(v) => updateMetadata({ units: v })}
                    keyProp="label"
                    valueProp="value"
                    keyLabel="Label (shown)"
                    valueLabel="Value (stored)"
                    helperText="Divisions/units offered on report forms."
                  />
                  <PairListField
                    anchor="region_filter_options"
                    label="Region filter options"
                    value={meta.region_filter_options ?? null}
                    onChange={(v) =>
                      updateMetadata({ region_filter_options: v })
                    }
                    keyProp="label"
                    valueProp="value"
                    keyLabel="Label (shown)"
                    valueLabel="Value (stored)"
                    helperText="Regions offered when filtering submissions."
                  />
                  <JsonField
                    anchor="field_configurations"
                    label="field_configurations (JSON)"
                    helperText="Per-field form overrides. Freeform by design — no typed UI."
                    value={fieldConfigurationsJson}
                    onChange={(v) => {
                      setFieldConfigurationsJson(v);
                      const result = tryParseJson<Record<string, unknown>>(
                        "field_configurations",
                        v,
                        true,
                      );
                      if (result.ok)
                        updateMetadata({ field_configurations: result.value });
                    }}
                    error={jsonErrors["field_configurations"]}
                  />
                </Stack>
              </TabSection>
            )}

            {activeTab === "notes" && (
              <TabSection>
                <TabIntro tab="notes" />
                <Grid container spacing={3}>
                  {RESPONSE_NOTE_SLOTS.map(({ key, label }) => (
                    <Grid key={key} size={{ xs: 12, md: 6 }}>
                      <ResponseNoteField
                        anchor="response_notes"
                        label={label}
                        value={config.response_notes?.[key] ?? null}
                        onChange={(v) =>
                          updateColumn("response_notes", {
                            ...(config.response_notes ?? {}),
                            [key]: v,
                          })
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </TabSection>
            )}

            {activeTab === "json" && (
              <TabSection>
                <TabIntro tab="json" />
                <Stack spacing={3}>
                  <JsonField
                    label="metadata (full JSON)"
                    helperText="Mirrors the typed metadata tabs. Saving here overwrites them."
                    value={metadataJson}
                    onChange={(v) => {
                      setMetadataJson(v);
                      const result = tryParseJson<OrgConfigMetadata>(
                        "metadata",
                        v,
                        false,
                      );
                      if (result.ok && result.value)
                        setConfig((prev) =>
                          prev ? { ...prev, metadata: result.value! } : prev,
                        );
                    }}
                    error={jsonErrors["metadata"]}
                    minRows={12}
                  />
                </Stack>
              </TabSection>
            )}

            {activeTab === "api_keys" && orgId && (
              <TabSection>
                <TabIntro tab="api_keys" />
                <M2mKeysPanel orgId={orgId} />
              </TabSection>
            )}

            {activeTab === "sso" && orgId && (
              <TabSection>
                <TabIntro tab="sso" />
                <SsoConnectionsPanel orgId={orgId} />
              </TabSection>
            )}

            {activeTab === "directory" && orgId && (
              <TabSection>
                <TabIntro tab="directory" />
                <OrgDirectoryPanel orgId={orgId} />
              </TabSection>
            )}

            {activeTab === "branding" && orgId && (
              <TabSection>
                <TabIntro tab="branding" />
                <OrgBrandingPanel orgId={orgId} />
              </TabSection>
            )}
          </Box>
        </Box>

        {/* The Access panels write on their own, so the form footer would be
            lying about what it saves. Hide it there rather than disable it. */}
        {!SELF_SAVING_TABS.has(activeTab) && (
          <div className="actions">
            {saveError && (
              <Alert severity="error" sx={{ flex: 1 }}>
                {saveError}
              </Alert>
            )}
            {dirtyTabs.size > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                {dirtyTabs.size === 1
                  ? "1 section changed"
                  : `${dirtyTabs.size} sections changed`}
              </Typography>
            )}
            <Button onClick={handleReset} disabled={!isDirty || isSaving}>
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!isDirty || isSaving || hasJsonError}
            >
              {isSaving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        )}

        <Snackbar
          open={saveSuccess}
          autoHideDuration={3000}
          onClose={() => setSaveSuccess(false)}
        >
          <Alert severity="success" onClose={() => setSaveSuccess(false)}>
            Saved
          </Alert>
        </Snackbar>
      </Paper>
    </DevPage>
  );
};

// `anchor` is for the tabs that ARE a single field (notify, templates,
// export_configuration) — search lands on the section itself.
const TabSection = ({
  children,
  anchor,
}: {
  children: ReactNode;
  anchor?: string;
}) => (
  <Box sx={{ py: 1 }} data-field={anchor}>
    {children}
  </Box>
);

const JsonField: FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
  helperText?: string;
  minRows?: number;
  anchor?: string;
}> = ({ label, value, onChange, error, helperText, minRows = 6, anchor }) => (
  <TextField
    data-field={anchor}
    fullWidth
    multiline
    minRows={minRows}
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    error={!!error}
    helperText={error ?? helperText ?? " "}
    sx={{ fontFamily: "monospace" }}
    inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
  />
);
